
import { useEffect, useState } from 'react';

interface TestimonialsProps {
  scrollY: number;
}

export default function Testimonials({ scrollY }: TestimonialsProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('testimonials');
      if (element) {
        const rect = element.getBoundingClientRect();
        setIsVisible(rect.top < window.innerHeight * 0.7);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const testimonials = [
    {
      id: 1,
      name: "Luna Martinez",
      role: "Wellness Coach",
      image: "https://public.readdy.ai/ai/img_res/724782b4ecf99af129ab5e0a28b6ad27.jpg",
      text: "Sacred Frequency has completely transformed my daily rituals. The herbal teas are divine, and each product feels like it carries genuine healing energy. My clients always ask about the beautiful glow I've developed since incorporating these sacred products into my routine."
    },
    {
      id: 2,
      name: "Aria Thompson",
      role: "Yoga Instructor",
      image: "https://readdy.ai/api/search-image?query=Peaceful%20yoga%20instructor%20with%20radiant%20skin%20in%20meditation%20pose%20surrounded%20by%20candles%20and%20sacred%20wellness%20products%20with%20warm%20earth%20tone%20lighting&width=300&height=300&seq=testimonial-2&orientation=squarish",
      text: "The Divine Glow Hair Oil is absolutely magical! My hair has never felt so nourished and healthy. I love how each Sacred Frequency product is crafted with such intention and care. It's like having a spa ritual at home every day."
    },
    {
      id: 3,
      name: "Sage Williams",
      role: "Holistic Therapist",
      image: "https://readdy.ai/api/search-image?query=Holistic%20therapist%20with%20glowing%20skin%20enjoying%20sacred%20bath%20ritual%20with%20candles%2C%20crystals%2C%20and%20herbal%20products%20in%20luxurious%20spa-like%20bathroom%20setting&width=300&height=300&seq=testimonial-3&orientation=squarish",
      text: "The Moonlight Bath Soak has become an essential part of my self-care routine. After stressful days working with clients, this ritual helps me reset and reconnect with my inner peace. The quality and energy of these products is unmatched."
    },
    {
      id: 4,
      name: "River Chen",
      role: "Meditation Teacher",
      image: "https://readdy.ai/api/search-image?query=Serene%20meditation%20teacher%20writing%20in%20sacred%20journal%20surrounded%20by%20crystals%2C%20candles%2C%20and%20spiritual%20wellness%20products%20in%20peaceful%20natural%20setting&width=300&height=300&seq=testimonial-4&orientation=squarish",
      text: "The Manifestation Journal has been a game-changer for my practice. The guided prompts are so thoughtfully crafted, and the quality is exceptional. Sacred Frequency truly understands the sacred nature of personal transformation."
    }
  ];

  return (
    <section id="testimonials" className="py-20 bg-gradient-to-b from-stone-50 to-amber-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-amber-800 to-red-700 bg-clip-text text-transparent">
            Sacred Stories
          </h2>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
            Hear from our community of souls who have embraced the Sacred Frequency way of living
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          <div className="overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}
            >
              {testimonials.map((testimonial, index) => (
                <div key={testimonial.id} className="w-full flex-shrink-0 p-8 md:p-12">
                  <div className="text-center">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-24 h-24 rounded-full mx-auto mb-6 object-cover shadow-lg"
                    />
                    <div className="mb-6">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="inline-block w-6 h-6 mx-1">
                          <i className="ri-star-fill text-amber-400 text-xl"></i>
                        </div>
                      ))}
                    </div>
                    <blockquote className="text-lg md:text-xl text-stone-700 leading-relaxed mb-6 italic">
                      "{testimonial.text}"
                    </blockquote>
                    <div>
                      <h4 className="text-xl font-bold text-stone-800 mb-1">{testimonial.name}</h4>
                      <p className="text-amber-600 font-medium">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentTestimonial ? 'bg-amber-600 scale-125' : 'bg-stone-300 hover:bg-stone-400'
                }`}
              />
            ))}
          </div>

          <button
            onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-stone-600 hover:text-amber-600 w-12 h-12 rounded-full shadow-lg transition-all duration-300 cursor-pointer flex items-center justify-center"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>
          
          <button
            onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-stone-600 hover:text-amber-600 w-12 h-12 rounded-full shadow-lg transition-all duration-300 cursor-pointer flex items-center justify-center"
          >
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
